import { useState } from "react";
import { DollarSign, Play, Loader2, CheckCircle, AlertTriangle, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export default function FeeAudit() {
  const { orgId, familyId } = useTenant();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);

  const { data: auditResults, isLoading } = useQuery({
    queryKey: ["fee-audit-results", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from("fee_audit_results")
        .select("*, instruments(name)")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (familyId) query = query.eq("family_id", familyId);

      const { data, error } = await query.limit(100);
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("run-fee-audit", {
        body: { orgId, familyId, thresholdPct: 5 },
      });
      if (error) throw error;
      toast.success(`Audit complete: ${data?.audited || 0} fees reviewed, ${data?.flagged || 0} discrepancies found`);
      queryClient.invalidateQueries({ queryKey: ["fee-audit-results"] });
    } catch (err) {
      toast.error("Failed to run audit: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setRunning(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from("fee_audit_results")
        .update({
          status,
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;
      toast.success(`Marked as ${status}`);
      queryClient.invalidateQueries({ queryKey: ["fee-audit-results"] });
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const flagged = auditResults?.filter(r => r.status === "flagged").length || 0;
  const totalDiscrepancy = auditResults?.filter(r => r.status === "flagged").reduce((s, r) => s + (r.discrepancy_amount || 0), 0) || 0;

  if (isLoading) return <div className="flex-1 p-8">Loading...</div>;

  return (
    <div className="flex-1 space-y-6 px-4 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fee Audit Agent</h1>
          <p className="text-muted-foreground">Compare charged fees against contractual rates to find discrepancies</p>
        </div>
        <Button onClick={runAudit} disabled={running}>
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
          {running ? "Auditing..." : "Run Audit"}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Total Reviewed</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{auditResults?.length || 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Flagged</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-amber-600">{flagged}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Potential Savings</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold font-mono text-green-600">${totalDiscrepancy.toLocaleString()}</div></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Fee Discrepancies</CardTitle>
          <CardDescription>Fees that differ from contractual rates by more than threshold</CardDescription>
        </CardHeader>
        <CardContent>
          {auditResults && auditResults.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Instrument</TableHead>
                  <TableHead>Fee Type</TableHead>
                  <TableHead className="text-right">Charged</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Difference</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditResults.map((result) => (
                  <TableRow key={result.id}>
                    <TableCell className="font-medium">{(result.instruments as { name?: string })?.name || "Unknown"}</TableCell>
                    <TableCell className="capitalize">{result.fee_type}</TableCell>
                    <TableCell className="text-right font-mono">${(result.charged_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">${(result.expected_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono text-amber-600">${(result.discrepancy_amount || 0).toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground ml-1">({(result.discrepancy_pct || 0).toFixed(1)}%)</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={result.status === "flagged" ? "destructive" : result.status === "confirmed" ? "default" : "secondary"}>
                        {result.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {result.status === "flagged" && (
                        <div className="flex justify-end gap-1">
                          <Button variant="outline" size="sm" onClick={() => updateStatus(result.id, "confirmed")}>
                            <CheckCircle className="h-3 w-3 mr-1" />Confirm
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => updateStatus(result.id, "disputed")}>
                            Dispute
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No fee audit results yet.</p>
              <p className="text-sm mt-2">Click "Run Audit" to compare charged fees against contractual rates.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
